/* V191 — Leads CSV Background Export Job Admin UI
 * Queue a background export, poll status, download the file when done.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_BADGE = {
  queued: "bg-zinc-700 text-zinc-200",
  running: "bg-amber-600 text-black animate-pulse",
  done: "bg-emerald-600 text-black",
  failed: "bg-rose-700 text-white",
};

export default function AdminLeadsExportTab() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [err, setErr] = useState("");
  const [activeJobId, setActiveJobId] = useState("");
  const pollRef = useRef(null);

  const token = () => localStorage.getItem("admin_token") || "";

  const loadList = async () => {
    if (!token()) { setErr("Admin token chýba. Prihlás sa znova."); return; }
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/leads/export-job/list?limit=30`,
        { headers: { "X-Admin-Token": token() } });
      setJobs(r.data.jobs || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadList(); }, []);

  // Polling for running/queued jobs every 2s
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === "queued" || j.status === "running");
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(loadList, 2000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // eslint-disable-next-line
  }, [jobs]);

  const queue = async () => {
    if (!token()) return;
    setQueueing(true); setErr("");
    try {
      const r = await axios.post(`${API}/leads/export-job/queue`, {},
        { headers: { "X-Admin-Token": token() } });
      if (r.data.ok) {
        setActiveJobId(r.data.job_id);
        await loadList();
      } else {
        setErr(`Job už beží: ${r.data.existing?.job_id || "—"}`);
        await loadList();
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "queue_failed");
    } finally { setQueueing(false); }
  };

  const download = async (job_id) => {
    if (!token()) return;
    try {
      const r = await axios.get(`${API}/leads/export-job/download/${job_id}`,
        { headers: { "X-Admin-Token": token() }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${job_id}.csv`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.response?.data?.detail || "download_failed");
    }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const fmtDuration = (a, b) => {
    if (!a || !b) return "—";
    try {
      const s = Math.round((new Date(b) - new Date(a)) / 1000);
      if (s < 60) return `${s}s`;
      return `${Math.floor(s / 60)}m ${s % 60}s`;
    } catch { return "—"; }
  };

  return (
    <div className="p-4 space-y-5" data-testid="leads-export-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          📊 Leads CSV Background Export
          <span className="text-[10px] tracking-widest bg-emerald-500 text-black px-2 py-0.5 font-black uppercase">V191</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Sync <code className="bg-zinc-800 px-1">/leads/export</code> je tvrdo cap-nutý na 5 000 riadkov. Tu spustíš background job ktorý vyexportuje
          <strong className="text-zinc-200"> celú leads kolekciu</strong> (100k+) do CSV a pošle ti email s odkazom keď je hotový.
        </p>
      </header>

      {/* Big action card */}
      <section className="border-2 border-zinc-800 bg-gradient-to-br from-zinc-950 to-black p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Nový export</div>
            <div className="text-sm text-zinc-300 mt-1">
              1-klik spustenie. Beží v pozadí, polluje sa každé 2s. Maximálne 1 export naraz.
            </div>
            {activeJobId && (
              <div className="text-[11px] text-amber-300 mt-2 font-mono">
                Active job: {activeJobId}
              </div>
            )}
          </div>
          <button onClick={queue}
                  disabled={queueing}
                  data-testid="leads-export-queue-btn"
                  className="text-sm px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
            {queueing ? "Zaraďujem…" : "🚀 Spustiť export"}
          </button>
          <button onClick={loadList}
                  className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            🔄 Refresh
          </button>
        </div>
      </section>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="leads-export-error">
          {err}
        </div>
      )}

      {/* Jobs table */}
      <section className="border border-zinc-800 bg-zinc-950" data-testid="leads-export-jobs-list">
        {loading && <div className="p-4 text-zinc-500 text-sm">Načítavam joby…</div>}
        {!loading && jobs.length === 0 && (
          <div className="p-6 text-center text-zinc-500 text-sm">
            Zatiaľ žiadne exporty. Spusti svoj prvý.
          </div>
        )}
        {!loading && jobs.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left p-2">Job ID</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Rows</th>
                <th className="text-right p-2">Size</th>
                <th className="text-left p-2">Queued</th>
                <th className="text-left p-2">Duration</th>
                <th className="text-left p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.job_id} className="border-t border-zinc-800 hover:bg-zinc-900/40"
                    data-testid={`leads-export-job-${j.job_id}`}>
                  <td className="p-2 font-mono text-zinc-400">{j.job_id}</td>
                  <td className="p-2">
                    <span className={`text-[10px] px-2 py-0.5 font-black uppercase tracking-wider ${STATUS_BADGE[j.status] || "bg-zinc-700 text-zinc-300"}`}>
                      {j.status}
                    </span>
                    {j.error && <div className="text-[10px] text-rose-400 mt-1">{j.error}</div>}
                  </td>
                  <td className="p-2 text-right font-mono">{j.row_count?.toLocaleString() || "—"}</td>
                  <td className="p-2 text-right font-mono text-zinc-400">{fmtBytes(j.file_size_bytes)}</td>
                  <td className="p-2 font-mono text-zinc-500">{(j.queued_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2 font-mono text-zinc-500">{fmtDuration(j.started_at, j.finished_at)}</td>
                  <td className="p-2">
                    {j.status === "done" && (
                      <button onClick={() => download(j.job_id)}
                              data-testid={`leads-export-download-${j.job_id}`}
                              className="text-[10px] px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-black font-black uppercase tracking-wider">
                        ⬇️ Download CSV
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed">
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">ℹ️ Notes</h3>
        <ul className="space-y-1 list-disc list-inside">
          <li>1-klik queue, ale len <strong className="text-zinc-200">1 job naraz</strong> (concurrency guard).</li>
          <li>Po dokončení sa pošle email na <code className="bg-zinc-800 px-1">ADMIN_EMAIL</code> z .env (cez Resend).</li>
          <li>CSV obsahuje stĺpce: email, url, lead_score, lead_grade, language, timestamp, status, analysis_count.</li>
          <li>Súbory sa ukladajú do <code className="bg-zinc-800 px-1">/app/backend/exports/&lt;job_id&gt;.csv</code>.</li>
        </ul>
      </section>
    </div>
  );
}
